import {
  Box,
  Input,
  Select,
  Textarea,
  Checkbox,
  CheckboxGroup,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import { useOverlay } from "@toss/use-overlay";
import {
  ColorIcon,
  colors,
  customEvent,
  HStack,
  OverlayModal,
  useResponsive,
  VStack,
} from "goi_common";
import React, { useState, useMemo } from "react";
import b2bOpsAxios from "@/servers/lib/axios";

const EMPLOYEE_SCALE_OPTIONS = [
  "100명 이하",
  "100~500명",
  "500~1000명",
  "1000명~5000명",
  "5000명 이상",
];
const CONSIDERATION_OPTIONS = [
  "상조/장례서비스 도입",
  "상조용품(일회용품) 제작",
  "근조화환/근조기 제작",
  "기타 제작",
];
const ADOPTION_OPTIONS = [
  "네 (업체 변경/추가를 고민하고 있어요)",
  "아니오 (처음 도입해요)",
];

const parsingPhoneNumber = (num: string) => {
  return num
    .replace(/[^0-9]/g, "")
    .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, "$1-$2-$3")
    .replace(/(-{1,2})$/g, "");
};

export function useRecathModal() {
  const overlay = useOverlay();
  const { isBiggerThanMd } = useResponsive();

  // 서비스 소개서 다운로드 모달 오픈 함수 (기존 로직 재활용)
  const openServiceDownloadModal = () => {
    new Promise<boolean>((resolve) => {
      overlay.open(({ isOpen, close: onClose }) => (
        <OverlayModal
          onClose={onClose}
          isOpen={isOpen}
          closeOnOverlayClick
          modalContentStyle={{
            minWidth: isBiggerThanMd ? "500px" : "100%",
            minHeight: isBiggerThanMd ? "1000px" : "100%",
            height: isBiggerThanMd ? "1000px" : "100%",
            borderRadius: isBiggerThanMd ? "10px" : "0",
            overflow: "hidden",
          }}
          overlayModalContent={
            <LeadFormModal
              title="서비스 소개서 받아보기"
              onClose={onClose}
              onSuccess={() => {
                onClose();
                resolve(true);
              }}
            />
          }
        />
      ));
    }).then((yes) => {
      if (yes) {
        window.open(
          "https://drive.google.com/file/d/1zgNp7SxASVlVueieDPyi3WC_rslUoXMD/view",
          "_blank"
        );
      }
    });
  };

  // 리드 폼 모달 오픈 함수
  const openModal = () => {
    customEvent({ newGtm: { action: "consult-button-click" } });
    overlay.open(({ isOpen, close: onClose }) => (
      <OverlayModal
        onClose={onClose}
        isOpen={isOpen}
        closeOnOverlayClick
        modalContentStyle={{
          minWidth: isBiggerThanMd ? "500px" : "100%",
          minHeight: isBiggerThanMd ? "1000px" : "100%",
          height: isBiggerThanMd ? "1000px" : "100%",
          borderRadius: isBiggerThanMd ? "10px" : "0",
          overflow: "hidden",
        }}
        overlayModalContent={
          <LeadFormModal
            onClose={onClose}
            onSuccess={() => {
              onClose();
            }}
          />
        }
      />
    ));
  };

  return { openModal, openServiceDownloadModal };
}

function LeadFormModal({
  onClose,
  onSuccess,
  title,
}: {
  onClose: () => void;
  onSuccess: () => void;
  title?: string;
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    employee_scale: EMPLOYEE_SCALE_OPTIONS[0],
    consideration: [],
    adoption_status: ADOPTION_OPTIONS[1],
    question: "",
    memo: "",
    meeting_step:
      title === "서비스 소개서 받아보기" ? "서비스소개서" : "상담대기",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; phone?: string }>({});

  const handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  // 이메일, 핸드폰 validation 함수
  const validate = () => {
    const newErrors: { email?: string; phone?: string } = {};
    // 이메일 정규식
    const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/;
    // 핸드폰 번호 정규식 (숫자, +, -, 공백 허용, 8~20자)
    const phoneRegex = /^[0-9+\-\s]{8,20}$/;
    if (!emailRegex.test(form.email)) {
      newErrors.email = "올바른 이메일 주소를 입력해 주세요.";
    }
    if (!phoneRegex.test(form.phone)) {
      newErrors.phone = "올바른 연락처를 입력해 주세요.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 필수 입력값이 모두 입력되었는지 체크
  const isFormValid = useMemo(() => {
    return (
      form.name.trim() !== "" &&
      form.email.trim() !== "" &&
      form.company.trim() !== "" &&
      form.phone.trim() !== "" &&
      form.employee_scale.trim() !== "" &&
      form.consideration.length > 0 &&
      !errors.email &&
      !errors.phone
    );
  }, [form, errors]);

  const handleSubmit = async () => {
    if (!validate()) {
      alert("정보를 올바르게 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    try {
      await b2bOpsAxios.post("/api/lead", {
        ...form,
        consideration: form.consideration.join(", "),
        form_type: title || "조사복지 견적문의",
      });
      setDone(true);
      setTimeout(() => {
        onClose();
        onSuccess();
      }, 3000);
    } catch {
      alert("신청에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <VStack
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
        background="white"
        gap={20}
      >
        <ColorIcon name="icon-check-mono" color={colors.green500} size={48} />
        <Box className="subtitle_21_b">신청이 완료되었습니다.</Box>
      </VStack>
    );
  }

  return (
    <VStack
      width="100%"
      height="100%"
      background="white"
      padding="32px 24px"
      gap={20}
    >
      <HStack justifyContent="space-between" alignItems="center" width="100%">
        <Box className="subtitle_21_b">{title || "고이 비즈 도입 문의"}</Box>
        <Box onClick={onClose} style={{ cursor: "pointer" }}>
          <ColorIcon name="icon-x-mono" color={colors.gray500} size={24} />
        </Box>
      </HStack>{" "}
      <Box>
        <Box width="100%" className="body_16_m">
          담당자 성함 <b style={{ color: "red" }}>*</b>
        </Box>
        <Input
          borderColor={colors.gray300}
          required
          value={form.name}
          onChange={(e) => handleChange("name", e.target.value)}
        />{" "}
      </Box>
      <Box>
        <Box width="100%" className="body_16_m">
          업무 메일 주소 <b style={{ color: "red" }}>*</b>
        </Box>
        <Input
          borderColor={colors.gray300}
          required
          type="email"
          value={form.email}
          onChange={(e) => handleChange("email", e.target.value)}
        />
        {errors.email && (
          <Box color="red" fontSize="13px" marginTop="4px">
            {errors.email}
          </Box>
        )}
      </Box>
      <Box>
        <Box width="100%" className="body_16_m">
          회사명 <b style={{ color: "red" }}>*</b>
        </Box>
        <Input
          borderColor={colors.gray300}
          required
          value={form.company}
          onChange={(e) => handleChange("company", e.target.value)}
        />
      </Box>
      <Box>
        <Box width="100%" className="body_16_m">
          연락처 <b style={{ color: "red" }}>*</b>
        </Box>
        <Input
          borderColor={colors.gray300}
          required
          value={form.phone}
          onChange={(e) =>
            handleChange("phone", parsingPhoneNumber(e.target.value))
          }
        />
        {errors.phone && (
          <Box color="red" fontSize="13px" marginTop="4px">
            {errors.phone}
          </Box>
        )}
      </Box>
      <Box>
        <Box width="100%" className="body_16_m">
          임직원 규모 <b style={{ color: "red" }}>*</b>
        </Box>
        <Select
          borderColor={colors.gray300}
          value={form.employee_scale}
          onChange={(e) => handleChange("employee_scale", e.target.value)}
          required
        >
          {EMPLOYEE_SCALE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Select>
      </Box>
      <Box>
        <Box width="100%" className="body_16_m" marginBottom="8px">
          고려하고 있는 조사복지 <b style={{ color: "red" }}>*</b>
        </Box>
        <CheckboxGroup
          value={form.consideration}
          onChange={(v) => handleChange("consideration", v)}
        >
          <Stack direction="column">
            {CONSIDERATION_OPTIONS.map((opt) => (
              <Checkbox
                key={opt}
                value={opt}
                sx={{
                  ".chakra-checkbox__control": {
                    bg: "white",
                    borderColor: "gray.300",
                    _checked: {
                      bg: "#FF7A00", // 오렌지색 (colors.orange650)
                      borderColor: "#FF7A00",
                    },
                  },
                  ".chakra-checkbox__label": {
                    fontWeight: 500,
                  },
                }}
              >
                {opt}
              </Checkbox>
            ))}
          </Stack>
        </CheckboxGroup>
      </Box>
      <Box>
        <Box width="100%" className="body_16_m" marginBottom="8px">
          이미 조사복지 도입을 하고 있나요?
        </Box>
        <RadioGroup
          value={form.adoption_status}
          onChange={(v) => handleChange("adoption_status", v)}
        >
          <Stack direction="column">
            {ADOPTION_OPTIONS.map((opt) => (
              <Radio
                key={opt}
                value={opt}
                colorScheme="orange"
                borderColor="gray.300"
              >
                {opt}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </Box>
      <Box>
        <Box width="100%" className="body_16_m" marginBottom="8px">
          기타 궁금한 사항을 남겨주세요
        </Box>
        <Textarea
          borderColor={colors.gray300}
          placeholder="기타 궁금한 사항을 남겨주세요"
          value={form.question}
          onChange={(e) => handleChange("question", e.target.value)}
        />
      </Box>
      <button
        type="button"
        onClick={handleSubmit}
        style={{
          width: "100%",
          background: isFormValid ? colors.orange650 : colors.gray300,
          color: "white",
          borderRadius: "10px",
          padding: "12px 16px",
          fontSize: "16px",
          fontWeight: 600,
        }}
        disabled={submitting || !isFormValid}
      >
        상담 신청하기
      </button>
    </VStack>
  );
}
